/**
 * Durable offline queue backed by IndexedDB.
 * Survives refreshes and browser restarts; every op carries its own idempotency key.
 */

const DB_NAME = "okyanus-offline";
const STORE = "ops";
const VERSION = 1;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "op_id" });
        store.createIndex("created_at", "created_at");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function tx(mode, run) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const result = run(transaction.objectStore(STORE));
    transaction.oncomplete = () => resolve(result?.result ?? result);
    transaction.onerror = () => reject(transaction.error);
  });
}

export const newOpId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export async function putOp(op) {
  await tx("readwrite", (store) => store.put(op));
  return op;
}

export async function listOps() {
  const rows = await tx("readonly", (store) => store.getAll());
  return (rows || []).sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
}

export async function removeOp(opId) {
  await tx("readwrite", (store) => store.delete(opId));
}

export async function clearOps() {
  await tx("readwrite", (store) => store.clear());
}

/** 5s, 10s, 20s … capped at 5 minutes. */
export function backoffMs(attempts) {
  return Math.min(5000 * 2 ** Math.max(attempts - 1, 0), 300000);
}
